/**
 * Comms (audio-direction §5): garbled-voice babble per character through the radio chain, on the comms bus, with
 * the music ducked while a line is on air. `say` plans a line from the character's seed and the stress level,
 * renders it offline (a few ms), and transmits it with squelch and static. No real words.
 *
 * M1 has no comms panel or subtitles yet, so nothing in the game calls this: it is exposed on the audio feature
 * and the audition page (a DEV hotkey triggers a sample line), ready for the comms HUD.
 */
import type { AudioEngine } from '../engine'
import type { OfflineFactory } from '../sfx/render'
import { planUtterance, type Utterance } from './babble'
import type { CommsConfig } from './commsSchema'
import { renderUtterance } from './formant'
import { createRadio, type Radio, type Transmission } from './radio'
import { voiceFromSeed } from './voice'

export interface CommsLine {
  readonly utterance: Utterance
  readonly transmission: Transmission
}

export interface CommsSystem {
  readonly radio: Radio
  /** Lines on air right now. */
  readonly onAir: number
  /**
   * Speaks one babble line as character `seed` at `stress` 0..1 (`lineSeed` picks the "words"; defaults to a
   * counter). Resolves once the line is scheduled.
   */
  say(seed: number, stress: number, lineSeed?: number): Promise<CommsLine>
  dispose(): void
}

export function createComms(engine: Pick<AudioEngine, 'ac' | 'commsIn' | 'commsOnAir'>, cfg: CommsConfig, factory: OfflineFactory, sampleRate: number): CommsSystem {
  const ac = engine.ac
  const radio = createRadio(ac, engine.commsIn, cfg, 0xc0dde)
  let lines = 0
  let onAir = 0
  return {
    radio,
    get onAir(): number {
      return onAir
    },
    async say(seed: number, stress: number, lineSeed?: number): Promise<CommsLine> {
      const utterance = planUtterance(voiceFromSeed(seed, cfg), lineSeed ?? lines++, stress, cfg)
      const buffer = await renderUtterance(factory, utterance, cfg, sampleRate)
      const transmission = radio.transmit(buffer, ac.currentTime + 0.02)
      onAir++
      engine.commsOnAir(1)
      transmission.source.onended = (): void => {
        onAir--
        engine.commsOnAir(-1)
      }
      return { utterance, transmission }
    },
    dispose(): void {
      radio.dispose()
    },
  }
}
